import { Component, OnDestroy, OnInit, ElementRef, AfterViewInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { Observable, Subject, Subscription } from 'rxjs';
import { emailExpression, phoneExpression } from 'src/app/config/app.config';
import {
  OrderModel,
  PaynetOrderResultModel,
  InvalidBasketResultModel,
  OrderResponseStatus,
  OrderResultModel,
} from 'src/app/models/order.model';
import { BookingService } from 'src/app/services/booking.service';
import { CartService } from 'src/app/services/cart/cart.service';
import { BasketItemOption } from '../../models/product-order.model';
import { ActivatedRoute } from '@angular/router';
import { environment } from 'src/environments/environment';
import { PaymentSystem, PricingSchemas } from 'src/app/models/enums';
import Utils, { MessageType } from 'src/app/shared/utils';
import { BasketModel } from 'src/app/models/basket.model';
import { ProductTypeId } from 'src/app/models/enums/product-type.enum';
import { LOCALE_ID, Inject } from '@angular/core';
import { NavigationService } from 'src/app/services/navigation.service';
import { CartProductInfoProvider, CartProductInfo } from 'src/app/providers/cart-product-info.provider';
import { DialogService } from 'src/app/services/dialog.service';
import { SoloModeProvider } from 'src/app/providers/solo-mode.provider';

interface BookingResponse {
  type: string;
  response: InvalidBasketResultModel;
}

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss'],
})
export class CartComponent implements OnInit, OnDestroy, AfterViewInit {
  public productInfo: CartProductInfo;
  public displayedColumns: string[] = ['name', 'date', 'participants', 'price', 'actions'];
  public orderItems: BasketItemOption[] = [];
  public basketCost: number;
  public additionalFee: number;
  private readonly additionalStripeFee = 0.03;
  public totalCost: number;
  public order: Partial<OrderModel>;

  public step = 1;
  public personalInfo: FormGroup;
  public stripe$: Observable<any>;
  public waitingResponse: boolean;
  public steps: any[];
  public errorMessage: string;
  public isSuccessStatus: boolean;

  private stripeSubmit: Subject<any>;
  private subscriptions: Subscription;
  isSoloMode: boolean;

  constructor(
    private fb: FormBuilder,
    private cartService: CartService,
    private bookingService: BookingService,
    private route: ActivatedRoute,
    private navigationService: NavigationService,
    private cartProductInfoProvider: CartProductInfoProvider,
    private dialogService: DialogService,
    private soloModeProvider: SoloModeProvider,
    private elementRef: ElementRef<HTMLElement>,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  public get isPartnerUsingPaynet(): boolean {
    return this.productInfo?.partnerPaymentSystemId === PaymentSystem.PayNet;
  }

  public ngOnInit() {
    this.isSoloMode = this.soloModeProvider.isSoloMode;
    this.loadProductInfo();

    this.subscriptions = new Subscription();

    this.subscriptions.add(
      this.cartService.getList().subscribe((list: BasketItemOption[]) => {
        this.orderItems = [...list];

        if (this.isPartnerUsingPaynet) {
          this.totalCost = this.getTotalCost();
        } else {
          this.basketCost = this.getTotalCost();
          this.additionalFee = Math.round(this.basketCost * this.additionalStripeFee * 100) / 100;
          this.totalCost = this.basketCost + this.additionalFee;
        }
      })
    );

    this.route.queryParams.subscribe((params) => {
      const status = params['status'];

      if (status) {
        if (status === 'success') {
          this.showSuccessConfirmationScreen();
        } else {
          this.showFailScreen({ status: OrderResponseStatus.PaymentFailed });
        }
      }
    });

    this.waitingResponse = false;
    this.errorMessage = '';
    this.steps = [
      {
        count: 1,
        name: 'View',
        icon: 'search',
        errorMessage: `Shopping cart can't be empty`,
      },
      {
        count: 2,
        name: 'Order info',
        icon: 'person',
        errorMessage: 'Please complete all required fields.',
      },

      { count: 3, name: 'Payment', icon: 'credit_card' },
      { count: 4, name: 'Booked', icon: 'done' },
    ];

    this.personalInfo = this.fb.group(
      {
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        email: ['', [Validators.required, this.customEmailValidator]],
        emailConfirm: [''],
        phone: ['', [Validators.required, this.customPhoneValidator]],
        voucher: [''],
        comments: [''],
      },
      { validators: [this.confirmEmail] }
    );

    this.stripeSubmit = new Subject();
    this.stripe$ = this.stripeSubmit.asObservable();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngAfterViewInit(): void {
    Utils.postMessageToParentWindow(MessageType.focus, null);
  }

  private loadProductInfo() {
    const productInfo = this.cartProductInfoProvider.load();

    if (productInfo) {
      this.productInfo = productInfo;
    } else {
      console.error('Cart component: could not load product info');
    }
  }

  public getOrderData(): OrderModel {
    const timeZone = moment().parseZone().format().toString().substring(19);

    const timeZoneOffset = timeZone[0] === '+' ? timeZone.substring(1) : timeZone;
    const currency = this.productInfo.currency;
    const customer = this.personalInfo.value;
    const basket: BasketModel = { options: this.orderItems, timeZoneOffset };
    return { basket, customer, currency };
  }

  private getTotalCost() {
    return this.orderItems.reduce((sum, item) => (sum += item.totalPrice), 0);
  }

  getHotelPricingSchemasUi(basketItemOption: BasketItemOption) {
    return this.cartService.getHotelPricingSchemasUi(basketItemOption);
  }

  getAdditionalBedsQuantity(item) {
    const extraBedsPricingScheme = item.optionQuantities?.find((x) => x.pricingSchemaId === PricingSchemas.ExtraBed);

    if (!extraBedsPricingScheme) return;

    return extraBedsPricingScheme.quantity;
  }

  public removeProduct(index) {
    this.cartService.removeOption(index);
  }

  public async next() {
    this.errorMessage = '';
    switch (this.step) {
      case 1:
        if (this.orderItems.length > 0) {
          this.step += 1;
        } else {
          this.errorMessage = this.steps.find((step) => step.count === 1).errorMessage;
        }
        break;
      case 2:
        if (this.personalInfo.valid) {
          if (this.isPartnerUsingPaynet) {
            await this.placeOrder();
          } else {
            this.step += 1;
          }
        } else {
          this.errorMessage = this.steps.find((step) => step.count === 2).errorMessage;
        }
        break;
      case 3:
        break;
    }
  }

  private async placeOrder() {
    this.waitingResponse = true;
    const orderData = this.getOrderData();
    const result = await this.bookingService
      .placeOrder(orderData)
      .catch((error: Error) => ({ error } as OrderResultModel));

    if (result.status === OrderResponseStatus.Succeeded) {
      this.redirectToPaynetPaymentPage(result as PaynetOrderResultModel);
    } else {
      this.showErrorDialog(result, () => orderData);
      this.waitingResponse = false;
    }
  }

  private showErrorDialog(result: OrderResultModel, orderDataFn: () => OrderModel) {
    this.dialogService
      .openFailedOrderDialog(this.elementRef.nativeElement, result, orderDataFn)
      .afterClosed()
      .subscribe(() => {
        if (result.status === OrderResponseStatus.NoAvailability) {
          this.cartService.clearList();
          this.navigationService.goToList(this.productInfo);
        } else {
          this.goToStep(1);
        }
      });
  }

  public previous() {
    this.goToStep(this.step - 1);
  }

  public goToStep(n) {
    this.step = n;
  }

  public handleStripe({ type, response }: BookingResponse) {
    this.errorMessage = '';

    if (type === 'success') {
      this.showSuccessConfirmationScreen();
    } else {
      this.showFailScreen(response);
    }
  }

  public get isHotel() {
    return ProductTypeId.hotel === this.productInfo.productTypeId;
  }

  private showSuccessConfirmationScreen() {
    this.isSuccessStatus = true;
    this.goToStep(4);
    this.waitingResponse = false;
    this.cartService.clearList();
    this.bookingService.updateAvailability();
  }

  private showFailScreen(result: OrderResultModel) {
    this.isSuccessStatus = false;
    this.goToStep(4);
    this.showErrorDialog(result, () => this.getOrderData());
    this.waitingResponse = false;
  }

  private redirectToPaynetPaymentPage(paynetData: PaynetOrderResultModel) {
    const data = {
      host: environment.paynet.host,
      paymentId: paynetData.paynetPaymentId,
      signature: paynetData.paynetSignature,
    };
    Utils.postMessageToParentWindow(MessageType.paynet, data);
  }

  private confirmEmail(group: FormGroup) {
    const email = group.controls.email;
    const emailConfirm = group.controls.emailConfirm.value;

    if (email.value !== emailConfirm || !email.valid) {
      group.controls.emailConfirm.setErrors({ confirm: true });
    }
  }

  private customEmailValidator(control: AbstractControl) {
    return control.value.length === 0 || emailExpression.test(String(control.value).toLowerCase())
      ? null
      : { customEmail: true };
  }

  private customPhoneValidator(control: AbstractControl) {
    return control.value.length === 0 || phoneExpression.test(String(control.value).replace(/[(,),\s,-]/g, ''))
      ? null
      : { customPhone: true };
  }

  public backToList() {
    this.navigationService.goToList(this.productInfo);
  }

  public humaneDateTime(item: BasketItemOption) {
    const dateFormat = 'MMMM D';
    //TODO remove `item.startTime` it is a temporary solution until old items are  12hours from now
    const optionStartDateTime =
      item.productTypeId == ProductTypeId.hotel || !item.startTime
        ? moment(item.from).locale(this.locale)
        : moment(item.from + 'T' + item.startTime);

    const optionEndDateTime = moment(item.to).locale(this.locale);

    return item.productTypeId == ProductTypeId.hotel || !item.startTime
      ? `${optionStartDateTime.format(dateFormat)} - ${optionEndDateTime.format(dateFormat)}`
      : `${optionStartDateTime.format(dateFormat)}, ${optionStartDateTime.format('H:mm')}`;
  }

  public humaneTime(time: string) {
    return moment(time).locale(this.locale).format('HH:mm');
  }
}
